import React from "react";
import { Box, Chip, useMediaQuery } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import { dt } from "../../../../shared/themes/redesignTokens";
import {
  ServiceTypeCode,
  PriorityTypeCode,
  BudgetTypeCode,
} from "../../../servicing/models/models";
import { getUiLocale } from "../../../../shared/utils/relativeTime";

import { formatUiDate } from "../../../../shared/utils/dateFormat";

const priorityColors: Record<number, { bg: string; text: string }> = {
  [PriorityTypeCode.urgent]: { bg: "#FEE2E2", text: "#DC2626" },
  [PriorityTypeCode.high]: { bg: "#FEF3C7", text: "#D97706" },
  [PriorityTypeCode.low]: { bg: "#F3F4F6", text: "#6B7280" },
};

interface RequestDetailsPanelProps {
  serviceRequest: PublicServiceRequest;
}

const DetailChip = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: { bg: string; text: string };
}) => (
  <Box sx={{ flex: "1 1 160px", minWidth: "140px" }}>
    <Box
      sx={{
        fontFamily: dt.font,
        fontSize: "11px",
        fontWeight: 500,
        color: dt.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        mb: 0.5,
      }}
    >
      {label}
    </Box>
    {color ? (
      <Chip
        label={value}
        size="small"
        sx={{
          fontFamily: dt.font,
          fontSize: "12px",
          fontWeight: 600,
          bgcolor: color.bg,
          color: color.text,
          height: 24,
        }}
      />
    ) : (
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "13px",
          fontWeight: 500,
          color: dt.textPrimary,
        }}
      >
        {value}
      </Box>
    )}
  </Box>
);

const RequestDetailsPanel = ({ serviceRequest }: RequestDetailsPanelProps) => {
  const { translate } = useTranslation(["serviceOffers", "servicing"]);
  const isDesktop = useMediaQuery("(min-width:1025px)");
  const sr = serviceRequest;

  const getServiceTypeLabel = (code: ServiceTypeCode): string => {
    const map: Record<number, string> = {
      [ServiceTypeCode.onSiteRepair]: translate("rfsDetails.serviceTypes.onSiteRepair"),
      [ServiceTypeCode.maintenance]: translate("rfsDetails.serviceTypes.maintenance"),
      [ServiceTypeCode.partsReplacement]: translate("rfsDetails.serviceTypes.partsReplacement"),
    };
    return map[code] || String(code);
  };

  const getPriorityLabel = (code: PriorityTypeCode): string => {
    const map: Record<number, string> = {
      [PriorityTypeCode.urgent]: translate("rfsDetails.priorities.urgent"),
      [PriorityTypeCode.high]: translate("rfsDetails.priorities.high"),
      [PriorityTypeCode.low]: translate("rfsDetails.priorities.low"),
    };
    return map[code] || String(code);
  };

  const getServiceDateLabel = (): string => {
    if (sr.flexibleDates) return translate("rfsDetails.flexibleDates");
    if (sr.serviceStartDate && sr.serviceEndDate) {
      return translate("rfsDetails.dateRange", {
        from: formatUiDate(sr.serviceStartDate),
        to: formatUiDate(sr.serviceEndDate),
      });
    }
    if (sr.serviceStartDate) {
      return translate("rfsDetails.specificDate", {
        date: formatUiDate(sr.serviceStartDate),
      });
    }
    return translate("rfsDetails.flexibleDates");
  };

  const getBudgetLabel = (): string => {
    if (!sr.budgetEnabled) return translate("rfsDetails.noBudget");
    if (sr.budgetTypeCode === BudgetTypeCode.fixed && sr.fixedBudget) {
      return translate("rfsDetails.fixedBudget", {
        amount: `\u20AC${sr.fixedBudget.toLocaleString(getUiLocale())}`,
      });
    }
    if (sr.budgetTypeCode === BudgetTypeCode.range && sr.budgetRangeFrom != null) {
      return translate("rfsDetails.budgetRange", {
        from: `\u20AC${sr.budgetRangeFrom.toLocaleString(getUiLocale())}`,
        to: `\u20AC${(sr.budgetRangeTo || 0).toLocaleString(getUiLocale())}`,
      });
    }
    if (sr.budgetTypeCode === BudgetTypeCode.requestQuote) {
      return translate("rfsDetails.requestQuote");
    }
    return translate("rfsDetails.noBudget");
  };

  const filesCount = sr.files?.length || 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Top: Machine image + details */}
      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          flexDirection: isDesktop ? "row" : "column",
        }}
      >
        {/* Machine image — fallback: user image → catalog image */}
        {(sr.machine?.imageUrl || sr.machine?.catalogImageUrl) && (
          <Box
            component="img"
            src={sr.machine.imageUrl || sr.machine.catalogImageUrl}
            alt={sr.machine.name}
            sx={{
              width: 120,
              height: 120,
              objectFit: "cover",
              borderRadius: dt.radiusSm,
              border: `1px solid ${dt.cardBorder}`,
              flexShrink: 0,
            }}
          />
        )}

        {/* Machine info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "16px",
              fontWeight: 700,
              color: dt.textPrimary,
              mb: 0.5,
            }}
          >
            {sr.machine?.name}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              rowGap: 0.25,
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textSecondary,
              lineHeight: 1.6,
            }}
          >
            {sr.machine?.brandName && (
              <Box component="span">
                {sr.machine.brandName}
                {sr.machine.model ? ` ${sr.machine.model}` : ""}
              </Box>
            )}
            {sr.machine?.serialNumber && (
              <Box component="span" sx={{ color: dt.textMuted }}>
                {" \u00B7 "}
                {translate("rfsDetails.serialNumber")}: {sr.machine.serialNumber}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              fontFamily: dt.font,
              fontSize: "12px",
              color: dt.textMuted,
              mt: 0.25,
            }}
          >
            {sr.machine?.system && (
              <Box component="span">
                {translate("rfsDetails.system")}: {sr.machine.system}
              </Box>
            )}
            {sr.machine?.machineCategoryName && (
              <Box component="span">
                {sr.machine.system ? " \u00B7 " : ""}
                {translate("rfsDetails.category")}: {sr.machine.machineCategoryName}
              </Box>
            )}
            {sr.machine?.productionYear && (
              <Box component="span">
                {" \u00B7 "}
                {translate("rfsDetails.productionYear")}: {sr.machine.productionYear}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Detail chips grid */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          py: 1.5,
          borderTop: `1px solid ${dt.cardBorder}`,
          borderBottom: `1px solid ${dt.cardBorder}`,
        }}
      >
        <DetailChip
          label={translate("rfsDetails.serviceType")}
          value={getServiceTypeLabel(sr.serviceTypeCode)}
        />
        <DetailChip
          label={translate("rfsDetails.priority")}
          value={getPriorityLabel(sr.priorityTypeCode)}
          color={priorityColors[sr.priorityTypeCode]}
        />
        <DetailChip
          label={translate("rfsDetails.serviceDate")}
          value={getServiceDateLabel()}
        />
        <DetailChip
          label={translate("serviceOffers:budget")}
          value={getBudgetLabel()}
        />
      </Box>

      {/* Description */}
      {sr.description && (
        <Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 500,
              color: dt.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              mb: 0.5,
            }}
          >
            {translate("rfsDetails.description")}
          </Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textPrimary,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {sr.description}
          </Box>
        </Box>
      )}

      {/* Bottom row: Customer + dates + files */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          pt: 1,
          borderTop: `1px solid ${dt.cardBorder}`,
        }}
      >
        {/* Customer */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "1 1 200px" }}>
          {sr.customer?.logoUrl && (
            <Box
              component="img"
              src={sr.customer.logoUrl}
              alt={sr.customer.name}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                objectFit: "cover",
                border: `1px solid ${dt.cardBorder}`,
              }}
            />
          )}
          <Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 500,
                color: dt.textMuted,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {translate("rfsDetails.customerInfo")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: dt.textPrimary,
              }}
            >
              {sr.customer?.name}
            </Box>
          </Box>
        </Box>

        {/* Dates */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flex: "1 1 200px" }}>
          {sr.openDate && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 14, color: dt.textMuted }} />
              <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textSecondary }}>
                {translate("rfsDetails.openDate")}: {formatUiDate(sr.openDate)}
              </Box>
            </Box>
          )}
          {sr.createdDate && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 14, color: dt.textMuted }} />
              <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textSecondary }}>
                {translate("rfsDetails.createdDate")}: {formatUiDate(sr.createdDate)}
              </Box>
            </Box>
          )}
        </Box>

        {/* Files */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AttachFileIcon sx={{ fontSize: 14, color: dt.textMuted }} />
          <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textSecondary }}>
            {filesCount > 0
              ? translate("rfsDetails.filesCount", { count: filesCount })
              : translate("rfsDetails.noFiles")}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RequestDetailsPanel;
